import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { countries } from '../data/mockData';
import { CheckCircle } from 'lucide-react';

const HeroSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    aware: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <section className="relative">
      {/* Hero Background with Video */}
      <div className="relative min-h-[700px]">
        {/* Video Background */}
        <div 
          className="absolute inset-0 overflow-hidden bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1920')`
          }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1920"
            className="absolute w-full h-full object-cover"
          >
            <source
              src="https://customer-assets.emergentagent.com/job_fasttrack-visa/artifacts/eerhcurl_background_hero.mp4"
              type="video/mp4"
            />
          </video>
        </div>
        <div className="absolute inset-0 bg-[#1a3a5c]/70" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div className="text-white pt-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 uppercase tracking-wide">
                Unlock Your
                <br />
                American Dream
                <br />
                With The U.S.
                <br />
                Visa Program
              </h1>
              
              <p className="text-xl text-gray-200 mb-8">
                Invest in your future and embrace new opportunities in the United States
              </p>
            </div>

            {/* Right Form */}
            <div className="bg-white rounded-lg shadow-2xl p-8">
              <div className="text-center mb-6">
                <h3 className="text-[#1a3a5c] text-lg font-bold mb-1">
                  Ready to Take the First Step Towards Your American Dream?
                </h3>
                <p className="text-[#1a3a5c] text-sm font-semibold">
                  Investments Start at $500,000 Through the Visa Program
                </p>
                <p className="text-[#c94a4a] text-sm mt-2">
                  Leave your details below to schedule your consultation
                </p>
              </div>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-[#1a3a5c] mb-2">Thank You!</h4>
                  <p className="text-gray-600">Our team will contact you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        placeholder="First Name*"
                        className="border-gray-300 focus:border-[#1a3a5c] bg-gray-50"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Last Name*"
                        className="border-gray-300 focus:border-[#1a3a5c] bg-gray-50"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="Email*"
                        className="border-gray-300 focus:border-[#1a3a5c] bg-gray-50"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone*"
                        className="border-gray-300 focus:border-[#1a3a5c] bg-gray-50"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Select onValueChange={(value) => setFormData({ ...formData, country: value })}>
                      <SelectTrigger className="border-gray-300 bg-gray-50">
                        <SelectValue placeholder="Country of Residence*" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {countries.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <p className="text-sm text-gray-700 mb-2">
                      Are you aware that applying for this immigrant visa requires a minimum investment of $500,000? <span className="text-[#c94a4a]">(Required)</span>
                    </p>
                    <RadioGroup
                      value={formData.aware}
                      onValueChange={(value) => setFormData({ ...formData, aware: value })}
                      className="flex space-x-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="yes" />
                        <Label htmlFor="yes" className="text-gray-700">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="no" />
                        <Label htmlFor="no" className="text-gray-700">No</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="consent"
                      checked={formData.consent}
                      onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
                      className="mt-1"
                    />
                    <Label htmlFor="consent" className="text-xs text-gray-600 leading-relaxed">
                      I agree to receive marketing and service messages via WhatsApp and text (SMS) at the number I provided, including messages sent using automated technology. Message frequency varies. Message & data rates may apply. Reply STOP to cancel or HELP for help.
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#c94a4a] hover:bg-[#b33d3d] text-white py-6 rounded font-semibold text-base"
                  >
                    Get Your Free Consultation Today
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
